import { useState } from "react";
import { MessageSquare, Plus, Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { useCreateFeedback, useFeedback, useUpdateFeedback } from "@/hooks/usePlanner";
import type { FeedbackCategory, FeedbackStatus } from "@/services/plannerService";

const CATEGORIES: FeedbackCategory[] = ["bug", "feature", "question", "ops"];
const STATUSES: { key: FeedbackStatus; label: string }[] = [
  { key: "submitted", label: "Submitted" },
  { key: "triaged", label: "Triaged" },
  { key: "in_progress", label: "In progress" },
  { key: "resolved", label: "Resolved" },
  { key: "wont_fix", label: "Won't fix" },
];

const CATEGORY_STYLE: Record<string, string> = {
  bug: "text-destructive border-destructive/40",
  feature: "text-coral-dark border-coral/40",
  question: "text-teal-dark border-teal/50",
  ops: "text-muted-foreground border-border",
};

const STATUS_STYLE: Record<string, string> = {
  submitted: "bg-muted text-muted-foreground",
  triaged: "bg-coral-light text-coral-dark",
  in_progress: "bg-coral-light text-coral-dark",
  resolved: "bg-teal-light text-teal-dark",
  wont_fix: "bg-destructive/10 text-destructive",
};

const PRIORITIES = ["none", "low", "medium", "high"];

export default function PlannerFeedbackPage() {
  const { data: items = [], isLoading } = useFeedback();
  const createFeedback = useCreateFeedback();
  const updateFeedback = useUpdateFeedback();

  const [category, setCategory] = useState<FeedbackCategory>("bug");
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState("none");

  const add = () => {
    if (!title.trim()) return;
    createFeedback.mutate(
      { category, title: title.trim(), priority },
      { onSuccess: () => { setTitle(""); setPriority("none"); setCategory("bug"); } },
    );
  };

  const statusLabel = (s: string) => STATUSES.find((x) => x.key === s)?.label ?? s;

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center gap-3">
        <MessageSquare className="h-6 w-6 text-coral" />
        <div>
          <h1 className="text-xl font-bold tracking-tight text-foreground">Feedback</h1>
          <p className="text-sm text-muted-foreground">Team bugs, ideas, and questions — triaged to resolution.</p>
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        {/* Add row */}
        <div className="flex flex-wrap items-end gap-2 border-b border-border bg-muted/30 px-4 py-3">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Type</label>
            <Select value={category} onValueChange={(v) => setCategory(v as FeedbackCategory)}>
              <SelectTrigger className="h-9 w-36 capitalize"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">What's up?</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") add(); }}
              placeholder="Dashboard shows a CORS error on load"
              className="h-9 min-w-[240px]"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Priority</label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger className="h-9 w-28 capitalize"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={add} disabled={!title.trim() || createFeedback.isPending} className="h-9">
            <Plus className="mr-1 h-4 w-4" /> Submit
          </Button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/20">
                <TableHead className="min-w-[280px]">Title</TableHead>
                <TableHead className="w-[120px]">Type</TableHead>
                <TableHead className="w-[100px]">Priority</TableHead>
                <TableHead className="w-[150px]">Status</TableHead>
                <TableHead className="min-w-[160px]">Reporter</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                    <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading feedback…
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">
                    No feedback yet — submit the first above.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((f) => (
                  <TableRow key={f.id} className={cn((f.status === "resolved" || f.status === "wont_fix") && "opacity-70")}>
                    <TableCell className="font-medium">{f.title}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={cn("font-normal capitalize", CATEGORY_STYLE[f.category])}>
                        {f.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="capitalize text-muted-foreground">{f.priority}</TableCell>
                    <TableCell>
                      <Select
                        value={f.status}
                        onValueChange={(v) => updateFeedback.mutate({ id: f.id, body: { status: v as FeedbackStatus } })}
                      >
                        <SelectTrigger className={cn("h-8 w-[128px] border-0 font-semibold", STATUS_STYLE[f.status])}>
                          <SelectValue>{statusLabel(f.status)}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{f.reporter_email || "—"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
